/** Thin WebGL2 helpers. No abstraction beyond what the three passes need. */

export interface GLContext {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  maxPointSize: number;
  maxTextureSize: number;
  /** GPU name when the browser reveals it, otherwise the generic renderer string. */
  renderer: string;
  /** Additive blending into RGBA32F targets. Without it, field sums live in half floats. */
  floatBlend: boolean;
  /** Linear filtering of RGBA32F textures. */
  floatLinear: boolean;
}

export function initGL(canvas: HTMLCanvasElement): GLContext | null {
  const gl = canvas.getContext('webgl2', {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: false,
    preserveDrawingBuffer: false,
    powerPreference: 'high-performance',
  });
  if (!gl) return null;
  // Float render targets are what the simulation lives in. No fallback.
  if (!gl.getExtension('EXT_color_buffer_float')) return null;
  const range = gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE) as Float32Array;
  // Firefox and Safari answer with a generic string unless the user opted in; fine.
  const dbg = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = String(dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
  return {
    gl,
    canvas,
    maxPointSize: Math.max(1, range[1] ?? 1),
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    renderer,
    floatBlend: !!gl.getExtension('EXT_float_blend'),
    floatLinear: !!gl.getExtension('OES_texture_float_linear'),
  };
}

export function createProgram(gl: WebGL2RenderingContext, vsSrc: string, fsSrc: string, name: string): WebGLProgram {
  const vs = compile(gl, gl.VERTEX_SHADER, vsSrc, `${name}.vert`);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fsSrc, `${name}.frag`);
  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(prog);
    gl.deleteProgram(prog);
    throw new Error(`link ${name}: ${log}`);
  }
  return prog;
}

function compile(gl: WebGL2RenderingContext, type: number, src: string, name: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error(`createShader ${name}`);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`compile ${name}: ${log}\n${numbered(src)}`);
  }
  return sh;
}

function numbered(src: string): string {
  return src
    .split('\n')
    .map((l, i) => `${String(i + 1).padStart(4)}  ${l}`)
    .join('\n');
}

export type Uniforms<K extends string> = Record<K, WebGLUniformLocation | null>;

export function uniforms<K extends string>(gl: WebGL2RenderingContext, prog: WebGLProgram, names: readonly K[]): Uniforms<K> {
  const out = {} as Uniforms<K>;
  for (const n of names) out[n] = gl.getUniformLocation(prog, n);
  return out;
}

export interface TexSpec {
  width: number;
  height: number;
  internalFormat: number;
  format: number;
  type: number;
  filter: number;
}

export function createTexture(gl: WebGL2RenderingContext, spec: TexSpec): WebGLTexture {
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, spec.filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, spec.filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, spec.internalFormat, spec.width, spec.height, 0, spec.format, spec.type, null);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

export function createFramebuffer(gl: WebGL2RenderingContext, textures: WebGLTexture[], name: string): WebGLFramebuffer {
  const fbo = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  const buffers: number[] = [];
  textures.forEach((tex, i) => {
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0 + i, gl.TEXTURE_2D, tex, 0);
    buffers.push(gl.COLOR_ATTACHMENT0 + i);
  });
  gl.drawBuffers(buffers);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    throw new Error(`framebuffer ${name} incomplete: 0x${status.toString(16)}`);
  }
  return fbo;
}

/** A VAO with nothing in it. Vertex shaders that use gl_VertexID need one bound in core-profile-like drivers. */
export function emptyVAO(gl: WebGL2RenderingContext): WebGLVertexArrayObject {
  return gl.createVertexArray();
}

export function bindTextures(gl: WebGL2RenderingContext, textures: readonly WebGLTexture[]): void {
  textures.forEach((t, i) => {
    gl.activeTexture(gl.TEXTURE0 + i);
    gl.bindTexture(gl.TEXTURE_2D, t);
  });
}
